/**
 * usePopularNetworks Composable
 *
 * Fetches top cryptocurrencies by market cap from CoinGecko API
 * and filters them by availability in Changelly
 */

import { ref, computed } from 'vue';
import type { ChangellyCurrency } from './useChangelly';

interface CoinGeckoMarket {
  id: string;
  symbol: string;
  name: string;
  market_cap: number;
  market_cap_rank: number;
}

interface PopularNetwork {
  coingeckoId: string;
  symbol: string;
  name: string;
  marketCapRank: number;
  marketCap: number;
}

// Mapping from CoinGecko IDs to common crypto symbols
const COINGECKO_TO_SYMBOL_MAP: Record<string, string> = {
  'bitcoin': 'btc',
  'ethereum': 'eth',
  'binancecoin': 'bnb',
  'tether': 'usdt',
  'usd-coin': 'usdc',
  'wrapped-bitcoin': 'wbtc',
  'wrapped-btc': 'wbtc',
  'coinbase-wrapped-btc': 'cbbtc',
  'matic-network': 'matic',
  'avalanche-2': 'avax',
  'dai': 'dai',
};

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;
const CACHE_KEY = 'tokenbox_popular_networks';

interface CachedData {
  data: PopularNetwork[];
  timestamp: number;
}

// Mock data for development (to avoid hitting rate limits)
const MOCK_POPULAR_NETWORKS: PopularNetwork[] = [
  { coingeckoId: 'bitcoin', symbol: 'btc', name: 'Bitcoin', marketCapRank: 1, marketCap: 1000000000000 },
  { coingeckoId: 'ethereum', symbol: 'eth', name: 'Ethereum', marketCapRank: 2, marketCap: 500000000000 },
  { coingeckoId: 'tether', symbol: 'usdt', name: 'Tether', marketCapRank: 3, marketCap: 100000000000 },
  { coingeckoId: 'binancecoin', symbol: 'bnb', name: 'BNB', marketCapRank: 4, marketCap: 80000000000 },
  { coingeckoId: 'usd-coin', symbol: 'usdc', name: 'USD Coin', marketCapRank: 5, marketCap: 50000000000 },
];

export function usePopularNetworks() {
  const loading = ref(false);
  const error = ref<Error | null>(null);
  const popularNetworks = ref<PopularNetwork[]>([]);

  /**
   * Load cached data from localStorage
   */
  function loadCache(): CachedData | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const parsed: CachedData = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid
      if (now - parsed.timestamp < CACHE_DURATION) {
        return parsed;
      }

      // Cache expired
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch (err) {
      console.error('Failed to load cache:', err);
      return null;
    }
  }

  /**
   * Save data to cache
   */
  function saveCache(data: PopularNetwork[]) {
    try {
      const cacheData: CachedData = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      console.error('Failed to save cache:', err);
    }
  }

  /**
   * Fetch top cryptocurrencies from CoinGecko API
   */
  async function fetchFromCoinGecko(limit: number = 10): Promise<PopularNetwork[]> {
    const url = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${limit}&page=1&sparkline=false`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`CoinGecko API error: ${response.statusText}`);
    }

    const data: CoinGeckoMarket[] = await response.json();

    return data.map((coin) => ({
      coingeckoId: coin.id,
      symbol: COINGECKO_TO_SYMBOL_MAP[coin.id] || coin.symbol.toLowerCase(),
      name: coin.name,
      marketCapRank: coin.market_cap_rank,
      marketCap: coin.market_cap,
    }));
  }

  /**
   * Get popular networks (with caching)
   */
  async function getPopularNetworks(options?: {
    limit?: number;
    useMock?: boolean;
  }): Promise<PopularNetwork[]> {
    const { limit = 5, useMock = import.meta.env.DEV } = options || {};

    loading.value = true;
    error.value = null;

    try {
      // Use mock data in development
      if (useMock) {
        console.log('[usePopularNetworks] Using mock data');
        popularNetworks.value = MOCK_POPULAR_NETWORKS.slice(0, limit);
        return popularNetworks.value;
      }

      // Try to load from cache first
      const cached = loadCache();
      if (cached) {
        console.log('[usePopularNetworks] Using cached data');
        popularNetworks.value = cached.data.slice(0, limit);
        return popularNetworks.value;
      }

      // Fetch from CoinGecko
      console.log('[usePopularNetworks] Fetching from CoinGecko API');
      const data = await fetchFromCoinGecko(limit);

      // Save to cache
      saveCache(data);

      popularNetworks.value = data;
      return data;
    } catch (err) {
      console.error('[usePopularNetworks] Error:', err);
      error.value = err instanceof Error ? err : new Error('Unknown error');

      // Fallback to mock data on error
      popularNetworks.value = MOCK_POPULAR_NETWORKS.slice(0, limit);
      return popularNetworks.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Filter popular networks by available Changelly currencies
   */
  function filterByChangellyCurrencies(
    popularNetworks: PopularNetwork[],
    changellyCurrencies: ChangellyCurrency[]
  ): PopularNetwork[] {
    const changellyTickers = new Set(
      changellyCurrencies
        .filter((c) => c.enabled && c.enabledFrom)
        .map((c) => c.ticker.toLowerCase())
    );

    return popularNetworks.filter((network) => {
      // Try both symbol and coingeckoId
      return (
        changellyTickers.has(network.symbol.toLowerCase()) ||
        changellyTickers.has(network.coingeckoId.toLowerCase())
      );
    });
  }

  /**
   * Clear cache
   */
  function clearCache() {
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('[usePopularNetworks] Cache cleared');
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  }

  return {
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    popularNetworks: computed(() => popularNetworks.value),
    getPopularNetworks,
    filterByChangellyCurrencies,
    clearCache,
  };
}
